/**
 * FeedbackModal.tsx
 *
 * Modal for submitting help requests, bug reports, and ideas
 */

import React, { useState } from 'react';
import { useTheme } from '../../services/ThemeContext';
import { sendReport, ReportType } from '../../services/vialogosService';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultType?: ReportType;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, defaultType = 'help' }) => {
  const { currentTheme: colors } = useTheme();
  const [reportType, setReportType] = useState<ReportType>(defaultType);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await sendReport({
        type: reportType,
        message: message.trim(),
        email: email.trim() || undefined,
      });

      if (result.success) {
        setSubmitted(true);
        // Reset form after 2 seconds
        setTimeout(() => {
          setSubmitted(false);
          setMessage('');
          setEmail('');
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to send report. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeLabel = (type: ReportType): string => {
    switch (type) {
      case 'help':
        return 'Help / Question';
      case 'bug':
        return 'Bug Report';
      case 'idea':
        return 'Feature Idea';
      case 'error':
        return 'Error Report';
    }
  };

  const getTypePlaceholder = (type: ReportType): string => {
    switch (type) {
      case 'help':
        return 'Describe your question or what you need help with...';
      case 'bug':
        return 'Describe the bug you encountered. What did you expect to happen?';
      case 'idea':
        return 'Describe your feature idea or suggestion...';
      case 'error':
        return 'The error has been logged automatically. Add any additional details here...';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          style={{
            backgroundColor: colors.surface,
            borderRadius: 16,
            maxWidth: 500,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px',
              borderBottomWidth: 1,
              borderBottomColor: colors.border,
              borderBottomStyle: 'solid',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}
            >
              <h2
                style={{
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: colors.primary,
                  margin: 0,
                }}
              >
                {submitted ? '✓ Sent!' : 'Send Feedback'}
              </h2>
              <button
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  fontSize: 24,
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            {!submitted && (
              <p
                style={{
                  fontSize: 14,
                  color: colors.textMuted,
                  margin: 0,
                }}
              >
                Help improve Via Logos by sending feedback, bug reports, or ideas.
              </p>
            )}
          </div>

          {/* Body */}
          {!submitted && (
            <div
              style={{
                padding: '20px 24px',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {/* Type Selection */}
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: '600',
                    color: colors.textMain,
                    marginBottom: 8,
                  }}
                >
                  What type of feedback?
                </label>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  {(['help', 'bug', 'idea'] as ReportType[]).map(type => (
                    <button
                      key={type}
                      onClick={() => setReportType(type)}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 8,
                        border: `1px solid ${reportType === type ? colors.primary : colors.border}`,
                        borderStyle: 'solid',
                        backgroundColor: reportType === type ? `${colors.primary}15` : 'transparent',
                        color: reportType === type ? colors.primary : colors.textMain,
                        fontSize: 14,
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {getTypeLabel(type)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: '600',
                    color: colors.textMain,
                    marginBottom: 8,
                  }}
                >
                  Message <span style={{ color: colors.primary }}>*</span>
                </label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={getTypePlaceholder(reportType)}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: 12,
                    borderRadius: 8,
                    fontSize: 14,
                    lineHeight: '20px',
                    backgroundColor: colors.background,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderStyle: 'solid',
                    color: colors.textMain,
                    resize: 'vertical',
                    outline: 'none',
                    zIndex: 10001,
                    position: 'relative',
                  }}
                />
              </div>

              {/* Email (optional) */}
              <div style={{ marginBottom: 20 }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: '600',
                    color: colors.textMain,
                    marginBottom: 8,
                  }}
                >
                  Your Email <span style={{ color: colors.textMuted, fontWeight: 'normal' }}>(optional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 8,
                    fontSize: 14,
                    backgroundColor: colors.background,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderStyle: 'solid',
                    color: colors.textMain,
                    outline: 'none',
                    zIndex: 10001,
                    position: 'relative',
                  }}
                />
                <p
                  style={{
                    fontSize: 12,
                    color: colors.textMuted,
                    marginTop: 6,
                    margin: '6px 0 0 0',
                  }}
                >
                  Provide your email if you'd like a response
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: '#ef444420',
                    border: `1px solid #ef4444`,
                    borderStyle: 'solid',
                    color: '#ef4444',
                    fontSize: 14,
                  }}
                >
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {submitted && (
            <div
              style={{
                padding: '40px 24px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 64,
                  marginBottom: 16,
                }}
              >
                ✅
              </div>
              <p
                style={{
                  fontSize: 18,
                  color: colors.textMain,
                  marginBottom: 8,
                }}
              >
                Thank you for your feedback!
              </p>
              <p
                style={{
                  fontSize: 14,
                  color: colors.textMuted,
                }}
              >
                Your message has been sent to the developer.
              </p>
            </div>
          )}

          {/* Footer */}
          {!submitted && (
            <div
              style={{
                padding: '16px 24px',
                borderTopWidth: 1,
                borderTopColor: colors.border,
                borderTopStyle: 'solid',
                display: 'flex',
                gap: 12,
                justifyContent: 'flex-end',
              }}
            >
              <button
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  backgroundColor: 'transparent',
                  border: `1px solid ${colors.border}`,
                  borderStyle: 'solid',
                  color: colors.textMain,
                  fontSize: 14,
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !message.trim()}
                style={{
                  padding: '12px 24px',
                  borderRadius: 8,
                  backgroundColor: isSubmitting || !message.trim() ? `${colors.primary}40` : colors.primary,
                  border: 'none',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: '600',
                  cursor: isSubmitting || !message.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default FeedbackModal;
